// src/store/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../services/authService';

// Проверява дали JWT токенът е изтекъл, без да прави заявка към сървъра.
// Декодираме payload-а (средната част) и четем exp полето.
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp е в секунди, Date.now() е в милисекунди
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      // console.log('Starting login process...');
      const response = await authService.login(credentials);
      const { token, user } = response.data;

      // console.log('Login successful, saving to localStorage:', { token, user });

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      return {
        token,
        user: {
          id: user.id,
          username: user.username,
          roles: [user.role],
          role: user.role,
          email: user.email || `${user.username}@example.com`,
          fullName: user.fullName || user.username
        }
      };
    } catch (error) {
      console.error('Login thunk error:', error);
      authService.logout();
      return rejectWithValue({
        message: error.message || 'Грешка при влизане',
        status: error.status
      });
    }
  }
);

// Синхронизация на Redux със localStorage при hard refresh.
// Ако токенът е изтекъл — изчистваме сесията вместо да я зареждаме.
export const syncAuthFromLocalStorage = createAsyncThunk(
  'auth/syncFromLocalStorage',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        throw new Error('No session data');
      }

      // Проверяваме токена локално — ако е изтекъл, изчистваме
      if (isTokenExpired(token)) {
        // console.log('Token expired during sync, clearing session.');
        authService.logout();
        throw new Error('Token expired');
      }

      const user = JSON.parse(userStr);

      return {
        token,
        user: {
          id: user.id,
          username: user.username,
          roles: user.roles || [user.role],
          role: user.role,
          email: user.email || `${user.username}@example.com`,
          fullName: user.fullName || user.username
        }
      };
    } catch (error) {
      // console.log('No valid session found:', error.message);
      authService.logout();
      return rejectWithValue({ message: 'No session' });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token: null,
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    initialized: false
  },
  reducers: {
    logout: (state) => {
      authService.logout();
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state, action) => {
      state.initialized = action.payload;
    },
    // Извиква се от ProtectedRoute при навигация —
    // ако токенът е изтекъл, изчистваме сесията без заявка към сървъра
    expireSession: (state) => {
      authService.logout();
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.error = null;
        state.initialized = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.initialized = true;
      })

      // Sync from localStorage
      .addCase(syncAuthFromLocalStorage.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncAuthFromLocalStorage.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(syncAuthFromLocalStorage.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        state.initialized = true;
      });
  }
});

export const { logout, clearError, setInitialized, expireSession } = authSlice.actions;
export default authSlice.reducer;

// СЕЛЕКТОРИ
export const selectUser = (state) => state.auth?.user;
export const selectToken = (state) => state.auth?.token;
export const selectAuthLoading = (state) => state.auth?.loading || false;
export const selectAuthError = (state) => state.auth?.error;
export const selectIsAuthenticated = (state) => state.auth?.isAuthenticated || false;
export const selectIsInitialized = (state) => state.auth?.initialized || false;
export const selectIsAdmin = (state) => {
  const user = state.auth?.user;
  if (!user) return false;
  return (
    user.role === 'ADMIN' ||
    user.roles?.some((role) => role === 'ADMIN' || role === 'ROLE_ADMIN') ||
    false
  );
};